import { useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Chess } from 'chess.js';
import type { Color, Key } from 'chessground/types';
import { Board } from '@/chess/board';
import type { BoardMoveEvent } from '@/chess/board';
import { StockfishEngine, isStockfishInitError } from '@/chess/engine';
import { FixedNav, Caption, Card, Button, PulseDot } from '@/ui';
import { practiceApi } from './api';
import {
  buildPgn,
  practiceGameReducer,
  type PracticeGameState,
} from './practiceGameReducer';
import { CADENCE_TIMING } from './types';
import type { PracticeCadence, PracticeResult } from './types';

/**
 * Practice Game page — partie active Léon vs Stockfish (Salve P1 T4).
 *
 * Cycle :
 *  1. Mount → fetch game state (level, cadence, color, theme) via GET /games/{id}
 *  2. Init StockfishEngine via useEffect (lazy)
 *  3. dispatch START_GAME → state.kind = your_turn ou engine_thinking
 *  4. Si engine_thinking : useEffect appelle engine.playMove() → dispatch MOVE_ENGINE
 *  5. Si your_turn : Board interactif, onMove → dispatch MOVE_PLAYER
 *  6. À chaque MOVE_* → POST /games/{id}/move (idempotent par ply)
 *  7. À game_over → POST /games/{id}/finish → navigate /review/{id}
 *
 * Horloges : 2 timers (player + engine). Décrement 100ms tick.
 * Increment fischer appliqué au passage du tour.
 *
 * Garde-fous tablette : touch-action: none implicite via Board, ré-mount via
 * key={gameId} pour éviter cross-state entre parties successives.
 */

function calculateTimeMs(cadence: PracticeCadence, levelElo: number): number {
  // Temps de réflexion engine = clamp(cadence/40, 300, 5000)ms
  const initialMs = CADENCE_TIMING[cadence].initialMs;
  const perMove = Math.floor(initialMs / 40);
  // Niveau élevé = plus de temps de réflexion (jusqu'à 5s)
  const levelBonus = Math.max(0, (levelElo - 1320) * 2);
  return Math.max(300, Math.min(5_000, perMove + levelBonus));
}

interface ClockState {
  playerMs: number;
  engineMs: number;
}

export function PracticeGamePage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const gameId = id ? Number(id) : NaN;
  const navigate = useNavigate();

  const gameQ = useQuery({
    queryKey: ['practice', 'game', gameId],
    queryFn: () => practiceApi.findGame(gameId),
    enabled: !Number.isNaN(gameId),
  });

  const [state, dispatch] = useReducer(practiceGameReducer, { kind: 'idle' } as PracticeGameState);
  const engineRef = useRef<StockfishEngine | null>(null);
  const [engineReady, setEngineReady] = useState(false);
  const [engineError, setEngineError] = useState<string | null>(null);
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);
  const [clock, setClock] = useState<ClockState>({ playerMs: 0, engineMs: 0 });

  // Init engine (lazy + idempotent via ref).
  useEffect(() => {
    if (engineRef.current) return;
    const engine = new StockfishEngine();
    engineRef.current = engine;
    engine
      .init()
      .then(() => setEngineReady(true))
      .catch((err: unknown) => {
        const msg = isStockfishInitError(err) ? 'Moteur indisponible. Réessaie plus tard.' : 'Erreur d\'init moteur.';
        setEngineError(msg);
      });
    return () => {
      engine.destroy();
      engineRef.current = null;
    };
  }, []);

  // Bootstrap state quand le game est chargé + engine prêt
  useEffect(() => {
    if (!gameQ.data || state.kind !== 'idle' || !engineReady) return;
    const data = gameQ.data;
    // C2 — couleur résolue côté backend (mig 019), plus de Math.random() instable
    // au refresh. Le DTO garantit la valeur 'white' | 'black'.
    const playerColor = data.resolved_color as Color;
    // C1 — `starting_fen` exposé par le DTO (LEFT JOIN practice_themes côté
    // backend). Fallback startpos si pas de thème ou thème en position standard.
    const startingFen = pickStartingFen(data);
    // Initialise les horloges
    const { initialMs } = CADENCE_TIMING[data.cadence];
    setClock({ playerMs: initialMs, engineMs: initialMs });
    dispatch({
      type: 'START_GAME',
      setup: {
        gameId,
        levelElo: data.level_elo,
        playerColor,
        startingFen,
        startedAtMs: performance.now(),
      },
      now: performance.now(),
    });
  }, [gameQ.data, state.kind, engineReady, gameId]);

  // Engine joue si state = engine_thinking
  useEffect(() => {
    if (state.kind !== 'engine_thinking' || !engineRef.current || !engineReady) return;
    let cancelled = false;
    const engine = engineRef.current;
    const setup = state.setup;
    const thinkMs = calculateTimeMs(gameQ.data?.cadence ?? 'rapid_15_10', setup.levelElo);
    engine
      .playMove(state.fen, setup.levelElo, thinkMs)
      .then((result) => {
        if (cancelled || !result.bestmove) return;
        dispatch({ type: 'MOVE_ENGINE', uci: result.bestmove, now: performance.now() });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('[practice-arena] engine.playMove failed', err);
        setEngineError('Erreur de calcul du moteur. Abandon recommandé.');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.kind, engineReady]);

  // Tick horloge — 100ms interval. Décrément du clock du joueur actif.
  useEffect(() => {
    if (state.kind !== 'your_turn' && state.kind !== 'engine_thinking') return;
    const interval = setInterval(() => {
      setClock((c) => {
        if (state.kind === 'your_turn') {
          return { ...c, playerMs: Math.max(0, c.playerMs - 100) };
        }
        return { ...c, engineMs: Math.max(0, c.engineMs - 100) };
      });
    }, 100);
    return () => clearInterval(interval);
  }, [state.kind]);

  // Time-out detection : si playerMs ou engineMs = 0 → game_over
  useEffect(() => {
    if (state.kind !== 'your_turn' && state.kind !== 'engine_thinking') return;
    if (clock.playerMs === 0) {
      dispatch({ type: 'GAME_OVER_DETECTED', result: 'loss', now: performance.now() });
    } else if (clock.engineMs === 0) {
      dispatch({ type: 'GAME_OVER_DETECTED', result: 'win', now: performance.now() });
    }
  }, [clock.playerMs, clock.engineMs, state.kind]);

  // Increment Fischer : ajoute incrementMs au passage de tour
  const lastKindRef = useRef<PracticeGameState['kind']>('idle');
  useEffect(() => {
    if (state.kind === 'idle' || !gameQ.data) {
      lastKindRef.current = state.kind;
      return;
    }
    const lastKind = lastKindRef.current;
    lastKindRef.current = state.kind;
    const { incrementMs } = CADENCE_TIMING[gameQ.data.cadence];
    if (incrementMs > 0) {
      // Transition your_turn → engine_thinking → +incrementMs au joueur
      if (lastKind === 'your_turn' && state.kind === 'engine_thinking') {
        setClock((c) => ({ ...c, playerMs: c.playerMs + incrementMs }));
      }
      // Transition engine_thinking → your_turn → +incrementMs à l'engine
      if (lastKind === 'engine_thinking' && state.kind === 'your_turn') {
        setClock((c) => ({ ...c, engineMs: c.engineMs + incrementMs }));
      }
    }
  }, [state.kind, gameQ.data]);

  // Sauvegarde fil-de-l'eau : à chaque nouveau coup, POST /games/{id}/move
  const lastSavedPlyRef = useRef<number>(0);
  useEffect(() => {
    if (state.kind === 'idle') return;
    const history = state.history;
    if (history.length <= lastSavedPlyRef.current) return;
    const newEntries = history.slice(lastSavedPlyRef.current);
    lastSavedPlyRef.current = history.length;
    // Fire-and-forget — idempotence backend par (game_id, ply).
    for (const entry of newEntries) {
      practiceApi
        .recordMove(gameId, {
          ply: entry.ply,
          san: entry.san,
          uci: entry.uci,
          time_spent_ms: entry.timeSpentMs,
        })
        .catch((err: unknown) => {
          console.error('[practice-arena] recordMove failed', err);
        });
    }
  }, [state, gameId]);

  // Finalisation : à game_over, POST /finish avec retry exponential (H3 P2).
  // useMutation gère 3 retries (1s, 2s, 4s) — si tout échoue, on affiche un
  // bouton "Réessayer" plutôt que de laisser Léon coincé en silence.
  const finishMutation = useMutation({
    mutationFn: (payload: { result: PracticeResult; final_fen: string; pgn: string }) =>
      practiceApi.finishGame(gameId, payload),
    retry: 3,
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 10_000),
    onSuccess: (data) => {
      navigate(`/practice-arena/review/${gameId}`, {
        state: {
          unlockedBadges: data.unlocked_badges,
          xpAwarded: data.xp_awarded,
          xpAmount: data.xp_amount,
        },
      });
    },
    onError: (err: unknown) => {
      console.error('[practice-arena] finishGame failed after retries', err);
    },
  });
  useEffect(() => {
    if (state.kind !== 'game_over') return;
    if (finishMutation.isPending || finishMutation.isSuccess) return;
    if (finishMutation.isError) return; // attend interaction utilisateur via "Réessayer"
    const pgn = buildPgn(state);
    finishMutation.mutate({ result: state.result, final_fen: state.fen, pgn });
  }, [state, finishMutation]);

  // Calcule legal moves pour le board interactif
  const interactive = state.kind === 'your_turn';
  const legalMoves = useLegalMoves(state.kind === 'your_turn' ? state.fen : undefined);

  if (Number.isNaN(gameId)) {
    return (
      <div className="min-h-screen bg-ink-900 text-paper-50">
        <FixedNav />
        <main className="pt-20 px-4 max-w-3xl mx-auto">
          <p className="text-coral-500" data-testid="invalid-id">ID de partie invalide.</p>
        </main>
      </div>
    );
  }

  if (gameQ.isLoading) {
    return (
      <div className="min-h-screen bg-ink-900 text-paper-50">
        <FixedNav />
        <main className="pt-20 px-4 max-w-3xl mx-auto">
          <p className="text-ink-300" data-testid="loading">Chargement de la partie…</p>
        </main>
      </div>
    );
  }
  if (gameQ.isError || !gameQ.data) {
    return (
      <div className="min-h-screen bg-ink-900 text-paper-50">
        <FixedNav />
        <main className="pt-20 px-4 max-w-3xl mx-auto">
          <p className="text-coral-500" data-testid="load-error">Impossible de charger la partie.</p>
        </main>
      </div>
    );
  }

  const playerColor = state.kind === 'idle' ? 'white' : state.setup.playerColor;
  const turnColor = state.kind === 'your_turn' ? playerColor : playerColor === 'white' ? 'black' : 'white';

  return (
    <div className="min-h-screen bg-ink-900 text-paper-50" key={gameId}>
      <FixedNav />
      <main className="pt-20 pb-12 px-4 md:px-8 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <PulseDot color="var(--orange-500)" />
          <Caption color="orange">Practice Arena</Caption>
        </div>
        <h1 className="text-heading-1 font-display font-semibold mb-1">
          {`Partie classique — Stockfish ELO ${gameQ.data.level_elo}`}
        </h1>
        <p className="text-ink-300 text-body-sm mb-6">
          {CADENCE_TIMING[gameQ.data.cadence].label} · Tu joues {playerColor === 'white' ? 'les Blancs' : 'les Noirs'}
        </p>

        {engineError && (
          <Card accent="coral" className="mb-4">
            <p className="text-coral-500 text-body-sm" data-testid="engine-error">
              {engineError}
            </p>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">
          {/* Board */}
          <div className="flex flex-col gap-3">
            <Board
              {...(state.kind !== 'idle' ? { fen: state.fen } : {})}
              orientation={playerColor}
              interactive={interactive}
              {...(state.kind !== 'idle' && state.lastMove ? { lastMove: state.lastMove } : {})}
              {...(legalMoves ? { legalMoves } : {})}
              turnColor={turnColor}
              engineThinking={state.kind === 'engine_thinking'}
              onMove={(m: BoardMoveEvent) => {
                if (state.kind !== 'your_turn') return;
                dispatch({
                  type: 'MOVE_PLAYER',
                  from: m.from,
                  to: m.to,
                  ...(m.promotion ? { promotion: m.promotion } : {}),
                  now: performance.now(),
                });
              }}
            />
          </div>

          {/* Info panel */}
          <div className="flex flex-col gap-4">
            {/* Horloges */}
            <Card accent="orange" padding="compact">
              <Caption color="orange">Horloges</Caption>
              <div className="mt-2 flex flex-col gap-2">
                <ClockRow label="Stockfish" ms={clock.engineMs} active={state.kind === 'engine_thinking'} />
                <ClockRow label="Toi" ms={clock.playerMs} active={state.kind === 'your_turn'} />
              </div>
            </Card>

            {/* Historique */}
            <Card padding="compact">
              <Caption>Historique</Caption>
              <ol className="mt-2 text-body-sm font-mono space-y-0.5 max-h-72 overflow-y-auto" data-testid="move-history">
                {state.kind !== 'idle' && state.history.length === 0 && (
                  <li className="text-ink-300">—</li>
                )}
                {state.kind !== 'idle' &&
                  state.history.map((entry) => (
                    <li
                      key={entry.ply}
                      className={entry.by === 'player' ? 'text-paper-50' : 'text-ink-300'}
                    >
                      {Math.ceil(entry.ply / 2)}. {entry.by === 'engine' ? '... ' : ''}
                      {entry.san}
                    </li>
                  ))}
              </ol>
            </Card>

            {/* Controls */}
            <Card padding="compact">
              <div className="flex flex-col gap-2">
                {!showAbandonConfirm && (
                  <Button
                    variant="danger"
                    onClick={() => setShowAbandonConfirm(true)}
                    disabled={state.kind === 'game_over' || state.kind === 'idle'}
                    data-testid="abandon-btn"
                  >
                    🏳 Abandonner
                  </Button>
                )}
                {showAbandonConfirm && (
                  <div className="flex flex-col gap-2" data-testid="abandon-confirm">
                    <p className="text-body-sm text-ink-300">Sûr ? La partie sera comptée comme abandon.</p>
                    <div className="flex gap-2">
                      <Button
                        variant="danger"
                        onClick={() => dispatch({ type: 'ABANDON', now: performance.now() })}
                        data-testid="abandon-confirm-yes"
                      >
                        Oui, abandonner
                      </Button>
                      <Button variant="secondary" onClick={() => setShowAbandonConfirm(false)}>
                        Continuer
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {state.kind === 'game_over' && (
              <div data-testid="game-over-card">
                <Card accent="orange">
                  <Caption color="orange">Partie terminée</Caption>
                  <p className="mt-2 text-body-lg font-medium">
                    {state.result === 'win' && 'Victoire'}
                    {state.result === 'loss' && 'Défaite'}
                    {state.result === 'draw' && 'Nulle'}
                    {state.result === 'abandoned' && 'Abandonnée'}
                  </p>
                  {!finishMutation.isError && (
                    <p className="mt-1 text-ink-300 text-body-sm">Redirection vers la review…</p>
                  )}
                </Card>
                {finishMutation.isError && (
                  <div className="mt-3" data-testid="finish-error">
                    <Card accent="coral">
                      <Caption color="coral">Sauvegarde impossible</Caption>
                      <p className="mt-2 text-body-sm text-ink-300">
                        Vérifie ta connexion et réessaie.
                      </p>
                      <div className="mt-3">
                        <Button
                          variant="primary"
                          accent="orange"
                          onClick={() => {
                            finishMutation.reset();
                            // Le useEffect game_over relancera mutate au prochain render.
                          }}
                        >
                          Réessayer
                        </Button>
                      </div>
                    </Card>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function ClockRow({ label, ms, active }: { label: string; ms: number; active: boolean }): JSX.Element {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  const formatted = `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  const lowTime = ms < 30_000;
  return (
    <div
      className={`flex items-center justify-between px-2 py-1.5 rounded ${
        active ? 'bg-orange-500/10 border border-orange-500/40' : 'bg-ink-900/40 border border-transparent'
      }`}
      data-testid={`clock-${label.toLowerCase()}`}
    >
      <span className="text-caption text-ink-300">{label}</span>
      <span
        className={`text-body-lg font-mono tabular-nums ${
          lowTime && active ? 'text-coral-500' : 'text-paper-50'
        }`}
      >
        {formatted}
      </span>
    </div>
  );
}

/**
 * Calcule la map des coups légaux à partir d'une FEN.
 * Utilisé pour activer le drag-drop + click-click sur Board (Chessground exige
 * une carte from → list of to).
 */
function useLegalMoves(fen: string | undefined): Map<Key, Key[]> | undefined {
  const [map, setMap] = useState<Map<Key, Key[]> | undefined>(undefined);
  useEffect(() => {
    if (!fen) {
      setMap(undefined);
      return;
    }
    const c = new Chess(fen);
    const moves = c.moves({ verbose: true });
    const out = new Map<Key, Key[]>();
    for (const m of moves) {
      const from = m.from as Key;
      const to = m.to as Key;
      const existing = out.get(from);
      if (existing) {
        existing.push(to);
      } else {
        out.set(from, [to]);
      }
    }
    setMap(out);
  }, [fen]);
  return map;
}

/**
 * Pioche le FEN de départ : si le DTO porte un `starting_fen` (résolu côté
 * backend via LEFT JOIN `practice_themes` — mig 019 Salve P2), on l'utilise.
 * Sinon, position initiale standard.
 *
 * Salve P2 C1 : le DTO `PracticeGameDetail` expose désormais `starting_fen`
 * directement, plus besoin de re-fetcher la table des thèmes côté frontend.
 */
const INITIAL_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

function pickStartingFen(data: { theme_slug: string | null; starting_fen: string | null }): string {
  return data.starting_fen ?? INITIAL_FEN;
}
